/**
 * One Survivor game: both teams with marks, scores, a Pick button beside
 * each team, the game state, the outcome tone and label, and a Priority
 * select on a picked card in a multi pick round (S2, S3, S4, S6, S7).
 */
import { Button, cn, Select } from "@sportsmet/ui";
import type { JSX } from "react";

import { TeamMark } from "../gameDetail/TeamMark";
import { resolvePlatePair } from "../gameDetail/teamPlateText";
import type { typePickTile, typePickTileSide } from "../pick/pickBoardModel";
import { PICK_SPREAD_LABEL } from "../pick/pickCopy";
import { PICK_TONE_CLASS, type typePickTone } from "./survivorCardModel";
import {
  SURVIVOR_PICK_LABEL,
  SURVIVOR_PICKED_LABEL,
  SURVIVOR_PRIORITY_LABEL,
} from "./survivorGameCopy";

export type typeMatchCardProps = {
  tile: typePickTile;
  gameStateLabel: string;
  homeLogoUrl: string | null;
  awayLogoUrl: string | null;
  tone: typePickTone;
  toneLabel: string | null;
  /** Set only when this card is picked and the round needs more than one pick. */
  priority: { value: number; count: number } | null;
  disabled: boolean;
  onSelectSide: (input: { eventId: string; teamId: string }) => void;
  onChangePriority: (input: { eventId: string; priority: number }) => void;
};

function TeamRow(props: {
  eventId: string;
  side: typePickTileSide;
  logoUrl: string | null;
  score: number | null;
  disabled: boolean;
  onSelectSide: (input: { eventId: string; teamId: string }) => void;
  /** The plate text the game's own pair resolved to, never side.teamAbbreviation
   *  on its own: two teams in one game must not plate alike (ISSUE-902 R4). */
  plateText: string;
}): JSX.Element {
  const { side } = props;
  const { availability } = side;
  const isSelected = availability.kind === "selected";
  const isUnavailable =
    availability.kind === "locked" || availability.kind === "lockedOut";
  const label = isSelected
    ? SURVIVOR_PICKED_LABEL
    : availability.kind === "locked" || availability.kind === "lockedOut"
      ? availability.label
      : SURVIVOR_PICK_LABEL;

  return (
    <div className="flex items-center gap-2">
      <TeamMark
        name={side.teamName}
        abbreviation={props.plateText}
        logoUrl={props.logoUrl}
        size="sm"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body-strong text-ink">
          {side.teamName}
        </span>
        {side.spreadLabel !== null && (
          <span className="text-caption text-ink-secondary">
            {side.spreadLabel} {PICK_SPREAD_LABEL}
          </span>
        )}
      </div>
      {props.score !== null && (
        <span className="text-body-strong tabular-nums">{props.score}</span>
      )}
      <Button
        size="sm"
        variant={isSelected ? "outline" : "secondary"}
        aria-pressed={isSelected}
        disabled={props.disabled || isUnavailable}
        data-testid={`survivor-pick-${props.eventId}-${side.teamId}`}
        onClick={() => {
          props.onSelectSide({ eventId: props.eventId, teamId: side.teamId });
        }}
      >
        {label}
        <span className="sr-only"> {side.teamName}</span>
      </Button>
    </div>
  );
}

export function MatchCard(props: typeMatchCardProps): JSX.Element {
  const { tile } = props;
  // Resolved once for the pair, so two teams in this game never plate alike
  // (ISSUE-902 R4).
  const { awayText, homeText } = resolvePlatePair({
    away: {
      name: tile.away.teamName,
      abbreviation: tile.away.teamAbbreviation,
    },
    home: {
      name: tile.home.teamName,
      abbreviation: tile.home.teamAbbreviation,
    },
  });
  return (
    <div
      data-testid={`survivor-match-${tile.eventId}`}
      data-tone={props.tone}
      className={cn(
        "flex flex-col gap-2 rounded-md p-3",
        PICK_TONE_CLASS[props.tone],
      )}
    >
      <TeamRow
        eventId={tile.eventId}
        side={tile.away}
        logoUrl={props.awayLogoUrl}
        score={tile.liveScore?.away ?? null}
        disabled={props.disabled}
        onSelectSide={props.onSelectSide}
        plateText={awayText}
      />
      <TeamRow
        eventId={tile.eventId}
        side={tile.home}
        logoUrl={props.homeLogoUrl}
        score={tile.liveScore?.home ?? null}
        disabled={props.disabled}
        onSelectSide={props.onSelectSide}
        plateText={homeText}
      />
      <div className="flex items-center justify-between gap-2">
        <span
          data-testid={`survivor-match-state-${tile.eventId}`}
          className="text-caption text-ink-secondary"
        >
          {props.gameStateLabel}
        </span>
        {props.toneLabel !== null && (
          <span
            data-testid={`survivor-match-tone-${tile.eventId}`}
            className="text-caption font-semibold text-ink"
          >
            {props.toneLabel}
          </span>
        )}
        {props.priority !== null && (
          <div
            data-testid={`survivor-priority-${tile.eventId}`}
            className="w-24"
          >
            <Select
              label={SURVIVOR_PRIORITY_LABEL}
              items={Array.from({ length: props.priority.count }, (_, i) => ({
                value: String(i + 1),
                label: String(i + 1),
              }))}
              value={String(props.priority.value)}
              disabled={props.disabled}
              onValueChange={(value) => {
                props.onChangePriority({
                  eventId: tile.eventId,
                  priority: Number(value),
                });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
